/**
 * 初回ログイン後に表示するページで使用するアセットをプリロードする
 */

const ASSETS_PRELOADED_KEY = 'va_fe_assets_preloaded';

/** 画像URLをプリロード（Image で読み込み） */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload: ${url}`));
    img.src = url;
  });
}

/** 初回ログイン済みか（このセッションでプリロード完了済みか） */
export function hasAssetsPreloaded(): boolean {
  return sessionStorage.getItem(ASSETS_PRELOADED_KEY) === 'true';
}

/** プリロード完了をマーク */
function markAssetsPreloaded(): void {
  sessionStorage.setItem(ASSETS_PRELOADED_KEY, 'true');
}

/**
 * ログイン後に表示する主要アセットをプリロードする
 * 初回ログイン時のみ実行し、完了まで TheLoading 表示を維持する想定
 */
export async function preloadCriticalAssets(): Promise<void> {
  if (hasAssetsPreloaded()) {
    return;
  }

  // ログイン後に表示する主要アセット（ヘッダー、試合一覧、共通アイコン、背景）
  const assetGlobs = [
    import.meta.glob<string>('@/assets/icon/header/*', { query: '?url', import: 'default', eager: true }),
    import.meta.glob<string>('@/assets/icon/sp/*', { query: '?url', import: 'default', eager: true }),
    import.meta.glob<string>('@/assets/icon/common/*', { query: '?url', import: 'default', eager: true }),
    import.meta.glob<string>('@/assets/icon/game/*', { query: '?url', import: 'default', eager: true }),
    import.meta.glob<string>('@/assets/background/*', { query: '?url', import: 'default', eager: true })
  ];
  const assetModules = Object.assign({}, ...assetGlobs);

  const loadPromises: Promise<void>[] = [];

  for (const mod of Object.values(assetModules)) {
    // eager: true のとき、import: 'default' で default export が直接返る
    const url = typeof mod === 'string' ? mod : (mod as { default?: string })?.default;
    if (url) {
      // 個別の失敗で全体をブロックしない
      loadPromises.push(preloadImage(url).catch(() => {}));
    }
  }

  // フォントの読み込み完了も待つ
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    loadPromises.push(document.fonts.ready as unknown as Promise<void>);
  }

  await Promise.all(loadPromises);
  markAssetsPreloaded();
}
